import { RefObject, useEffect, useState } from 'react';
import { CategoryCount, CategoryCountSelection, categoryCountKey, getCategoryCounts } from '../api/categoryCounts';

const BATCH_SIZE = 12;
const POLL_MS = 2500;

/** Only visible catalogue rows request exact totals; moving away cancels their work. */
export function useVisibleCategoryCounts(
  selections: CategoryCountSelection[],
  active: boolean,
  rootRef: RefObject<HTMLDivElement | null>,
): Record<string, CategoryCount> {
  const selectionKey = JSON.stringify(selections);
  const [snapshot, setSnapshot] = useState<{ selectionKey: string; counts: Record<string, CategoryCount> }>({ selectionKey: '', counts: {} });

  useEffect(() => {
    const root = rootRef.current;
    if (!active || !root) {
      setSnapshot({ selectionKey: '', counts: {} });
      return;
    }
    const candidates: CategoryCountSelection[] = JSON.parse(selectionKey);
    if (!candidates.length) return;
    const byKey = new Map(candidates.map((selection) => [categoryCountKey(selection), selection]));
    const visible = new Set<string>();
    const counts: Record<string, CategoryCount> = {};
    const fetchedAt: Record<string, number> = {};
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    let inFlight = false;

    const schedule = () => {
      clearTimeout(timer);
      if (inFlight || controller.signal.aborted) return;
      const remaining = Array.from(visible).filter((key) => !counts[key] || counts[key].count_status === 'pending');
      if (!remaining.length) return;
      const delay = Math.min(...remaining.map((key) => !counts[key] ? 0 : Math.max(0, (fetchedAt[key] || 0) + POLL_MS - Date.now())));
      timer = setTimeout(load, delay);
    };

    const load = async () => {
      if (controller.signal.aborted || inFlight) return;
      const keys = Array.from(visible).filter((key) => !counts[key] || (counts[key].count_status === 'pending' && Date.now() - fetchedAt[key] >= POLL_MS)).slice(0, BATCH_SIZE);
      if (!keys.length) { schedule(); return; }
      inFlight = true;
      try {
        // Reopening can recover a failed server job once; pending polls never restart it.
        const retryFailed = keys.some((key) => !counts[key]);
        const data = await getCategoryCounts(keys.map((key) => byKey.get(key)!), controller.signal, retryFailed);
        if (controller.signal.aborted) return;
        keys.forEach((key) => {
          const item = data.find((value) => value.key === key);
          counts[key] = item || { key, total: null, count_status: 'error' };
          // Never retry malformed responses indefinitely or accept a capped total.
          if (counts[key].total_capped || (counts[key].count_status === 'ready' && counts[key].total == null)) {
            counts[key] = { key, total: null, count_status: 'error' };
          }
          fetchedAt[key] = Date.now();
        });
      } catch {
        if (controller.signal.aborted) return;
        keys.forEach((key) => { counts[key] = { key, total: null, count_status: 'error' }; });
      } finally {
        inFlight = false;
        if (!controller.signal.aborted) {
          setSnapshot({ selectionKey, counts: { ...counts } });
          schedule();
        }
      }
    };

    const elements = Array.from(root.querySelectorAll<HTMLElement>('[data-category-count-key]'));
    let observer: IntersectionObserver | undefined;
    const updateVisibility = (element: HTMLElement, isVisible: boolean) => {
      const key = element.dataset.categoryCountKey;
      if (!key || !byKey.has(key)) return;
      if (isVisible) visible.add(key);
      else visible.delete(key);
    };
    const measure = () => {
      const bounds = root.getBoundingClientRect();
      elements.forEach((element) => {
        const box = element.getBoundingClientRect();
        updateVisibility(element, box.bottom >= bounds.top && box.top <= bounds.bottom);
      });
      schedule();
    };
    if (typeof IntersectionObserver !== 'undefined') {
      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => updateVisibility(entry.target as HTMLElement, entry.isIntersecting));
        schedule();
      }, { root, rootMargin: '80px 0px' });
      elements.forEach((element) => observer!.observe(element));
    } else {
      measure();
      root.addEventListener('scroll', measure);
      window.addEventListener('resize', measure);
    }
    return () => {
      controller.abort();
      clearTimeout(timer);
      observer?.disconnect();
      root.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
    };
  }, [active, selectionKey, rootRef]);

  return active && snapshot.selectionKey === selectionKey ? snapshot.counts : {};
}
